import { AlertOptions } from 'react-alert';
import { ReactNode, RefObject } from 'react';
import { NavLinkProps } from 'react-router-dom';
import { SerializedError } from '@reduxjs/toolkit';
import { QueryStatus } from '@reduxjs/toolkit/query';

import { Cart } from 'store/api/cart';
import { Event } from 'store/api/event';
import { Payout } from 'store/api/payout';
import { RoleData } from 'store/api/users';
import { Storage } from 'store/api/storage';
import { Employee } from 'store/api/employees';
import { Product } from 'store/api/product/types';
import { Category } from 'store/api/category/types';
import { FiservCaseDetail } from 'store/api/fiserv/types';
import { PaymentMethod as GlobalPaymentMethod } from 'store/api/payment-methods';
import {
  Role,
  Roles,
  Feature,
  CommissionType,
  BackOfficeRoles,
  NotificationType,
  PaymentMethodType,
  PriceApprovalStatus,
  NotificationChannel,
  PaymentIntentStatus,
} from 'types/enums';

export * from './icons';
export * from './enums';
export * from './titles';
export * from './colors';

export type { AppTheme } from '../config/themes/types';

export interface BaseHandleMutationProps<T> {
  error?: any;
  data?: T | void;
  reset?: () => void;
  status?: QueryStatus;
  showError: (error: string) => void;
  onSuccess?: (data?: T | void) => void;
  onError?: (errorMessage: any, error: any) => void;
}

export interface UseQueryReturnProps<T = unknown> {
  data?: T;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
  error?: Error | SerializedError;
}

export interface BaseResponse<T> {
  data: T;
  message: any;
  success: boolean;
  totaltime_pages: number;
  permission_updated: boolean;
}

export interface ForwardRefType<T = unknown> {
  refForward?: (
    element: T | null
  ) => RefObject<T | null> | void | null | undefined;
}

export interface RouterNavLinkProps extends NavLinkProps {
  label: string;
  disabled?: boolean;
  badgeCount?: number;
}

export interface BasePageProps {
  isError: boolean;
  error?: ReactNode;
  refetch: () => void;
  isLoading?: boolean;
}

export interface SafeRefetchProps {
  isLoading: boolean;
  refetch: () => void;
  isUninitialized: boolean;
}

export interface AlertOptionsType extends AlertOptions {
  title?: string;
  icon?: ReactNode;
  description?: Nullable<string>;
}

export type HandleMutationProps<T> = Omit<
  BaseHandleMutationProps<T>,
  'showError'
>;

export interface PaginationRequest {
  page?: number;
  limit?: number;
}

export interface AuthEmail {
  email: string;
  password: string;
}

export interface LocationState {
  backPath?: NavLinkProps['to'];
}

export interface BaseEntity extends IdObject {
  name: string;
}

export interface DateRange {
  to?: Date;
  from?: Date;
}

export interface UploadFile {
  src: any;
  file: File;
}

export interface IdObject<T = number> {
  id: T;
}

export type UnknownObject = Record<string, unknown>;

export type Nullable<T> = T | null | undefined;

export const NumPads = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
] as const;
export enum FileType {
  Image = 'image',
  Video = 'video',
  File = 'file',
}

export interface User extends IdObject {
  name: string;
  email: string;
  role: RoleData;
  photoUrl?: string;
  company?: Company;
  userType: UserType;
  employeeId?: number;
  features: Feature[];
  approvalPassword?: string;
  hasApprovalPassword?: boolean;
  // Set when the company owner revoked this user's access; null when active.
  deactivatedAt?: string | null;
}

export interface Person extends BaseEntity {
  zip: string;
  city: string;
  email: string;
  phone: string;
  state: string;
  address: string;
  lastName: string;
  photoUrl?: string;
  firstName: string;
  birthday?: string;
}

export interface Company extends IdObject {
  name: string;
  zip?: string;
  fax?: string;
  city?: string;
  email?: string;
  state?: string;
  phone?: string;
  address?: string;
  website?: string;
  photoUrl?: string;
  tipOptions: number[];
  processingFee: number;
  businessOwner?: string;
  isTipsEnabled?: boolean;
  isTipFeeEnabled?: boolean;
  notificationEmails: string[];
  notificationPhones: string[];
  isProcessingFeeEnabled: boolean;
  tipFeePercent?: number | string;
  isBrokenMissingEnabled?: boolean;
  isApprovalRequestEnabled: boolean;
  isEventCompetitionEnabled: boolean;
  brokenMissingPercent?: number | string;
}

export interface Media {
  id: number;
  url: string;
  name: string;
  size?: number;
  type: FileType;
  thumbnailUrl?: Nullable<string>;
}

export type TimeZone = {
  id: number;
  name: string;
  code: string;
  time_zone_code: string;
};

export type PaginationData<K extends string, T> = { count: number } & {
  [key in K]: T[];
};

export interface AuthEmail {
  email: string;
  password: string;
}

export interface SearchRequest {
  search?: string;
}

export type NumPadKey = (typeof NumPads)[number];

export type UserType = 'ops' | 'client';

export const UserTypes: Record<UserType, Role[]> = {
  ops: [Roles.SITE_ADMIN, Roles.BACKOFFICE],
  client: [
    Roles.COMPANY_OWNER,
    BackOfficeRoles.SALES_REP,
    BackOfficeRoles.ADMIN_REP,
    BackOfficeRoles.CUSTOMER_SERVICE_REP,
    BackOfficeRoles.RESEARCH_MANAGER,
    BackOfficeRoles.RESEARCH_REP,
    BackOfficeRoles.ACCOUNTING,
  ],
};

export interface PriceApproval extends IdObject {
  cart: Cart;
  user: User;
  event: Event;
  price: number;
  cartId: number;
  userId: number;
  eventId: number;
  comment?: string;
  quantity: number;
  company: Company;
  product: Product;
  storage: Storage;
  storageId: number;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  productId: number;
  status: PriceApprovalStatus;
}

export interface EventExpense extends IdObject {
  user: User;
  notes: string;
  amount: string;
  userId: number;
  company: Company;
  companyId: number;
  createdAt: string;
  categoryId: number;
  category: Category;
  receiptUrl?: string;
  paymentMethodId: number;
  updatedAt: Nullable<string>;
  deletedAt: Nullable<string>;
  paymentMethod: GlobalPaymentMethod;
}

export interface AccountingExpenseRentalDetail {
  renterName: string;
  notes?: string | null;
  uploadRefsJson?: unknown[];
  bookingDate?: string | null;
  agreementNumber?: string | null;
  reservationNumber?: string | null;
  estimatedAmountCents?: number | null;
  rentalCompany: 'AVIS' | 'OTHER' | 'BUDGET' | 'ENTERPRISE';
}

export interface AccountingExpense extends IdObject {
  amount: number;
  paidDate: string;
  company: Company;
  createdBy?: User;
  companyId: number;
  receiptUrl: string;
  paymentDate: string;
  description?: string;
  employees: Employee[];
  paymentMethodId?: number;
  events: AccountingEvent[];
  paymentMethod: GlobalPaymentMethod;
  rentalDetail?: AccountingExpenseRentalDetail | null;
  categories: (AccountingCategory & {
    ExpenseCategoryLink?: { amount?: number | string | null };
  })[];
}

export interface PaymentMethod {
  cloverAllocation?: CloverPartialAllocation;
  cloverTipAmount?: number;
  cloverCreatedTime?: string;
  amount: number;
  last4CC?: string;
  cardType?: string;
  id: string | number;
  holderName?: string;
  totalAmount: number;
  checkNumber?: number;
  phoneNumber?: string;
  driverLicense?: string;
  proccessingFee?: number;
  type: PaymentMethodType;
  cloverPaymentId?: string;
  paymentAuthNumber?: string;
  receivedCashAmount?: number;
}

export interface CommissionLevel extends IdObject {
  name: string;
  minSales: number;
  maxSales: number;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  percentage: number;
  commissionSetId: number;
}

export interface CommissionSet extends IdObject {
  name: string;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  description?: string;
  commissionLevels: CommissionLevel[];
}

export interface Commission {
  id: number;
  user?: User;
  cash?: number;
  amount: number;
  userId: number;
  employeeId: number;
  checkoutId?: number;
  type: CommissionType;
}

export interface AccountingEvent extends IdObject {
  event: Event;
  amount: number;
  eventId: number;
  percent: number;
  expenseId: number;
}

export interface CommonFieldProps {
  name: string;
  label?: string;
  controlErrorLabel?: boolean;
}

export interface AccountingCategory extends IdObject {
  name: string;
  description?: string;
}

export interface Timezone extends IdObject {
  code: string;
  state: string;
}

export interface UploadFile {
  src: any;
  file: File;
}

export interface Adjustment {
  label?: string;
  amount: number;
  eventName: string;
  checkoutId?: number | string;
}

export interface Paychecks {
  event: Event;
  totalPay: number;
  tipTotal: number;
  totalPayout: number;
  refundTotal: number;
  bonusesTotal: number;
  netSalesTotal: number;
  payoutItems: Payout[];
  advancesTotal: number;
  grossSalesTotal: number;
  chargebackTotal: number;
  commissionsTotal: number;
  miscPositiveTotal: number;
  miscNegativeTotal: number;
  freeShippingTotal: number;
  reimbursementsTotal: number;
  // Payroll settings: the automatic "Broken & missing products" deduction
  // (negative). Present only when the event has such a line.
  brokenMissingTotal?: number;
  refundAdjustments?: Adjustment[];
  chargebackAdjustments?: Adjustment[];
}

export interface NotificationPayload extends IdObject {
  title: string;
  text?: string;
  link?: string;
}

export interface Notification extends IdObject {
  userId?: number;
  isRead?: boolean;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  eventType: NotificationType;
  payload: NotificationPayload;
  channels: NotificationChannel[];
}

export interface NotificationReader extends IdObject {
  user: User;
  readAt: string;
}

export interface CloverPartialAllocation {
  version: 2;
  policy: 'PURCHASE_FIRST';
  requestedAmount: number;
  requestedFee: number;
  requestedTip: number;
  approvedAmount: number;
  processingFee: number;
  tipAmount: number;
  baseAmount: number;
  unpaidBase: number;
  unpaidFee: number;
  unpaidTip: number;
}

export interface CloverPayment extends IdObject {
  checkoutId?: number | null;
  issuesData?: {
    clixAllocation?: CloverPartialAllocation;
    partialPayment?: { requestedAmount: number; actualAmount: number };
  };
  type: string;
  last4: string;
  state: string;
  amount: number;
  authCode: string;
  cardType: string;
  offline: boolean;
  entryType: string;
  paymentId: string;
  taxAmount: number;
  voidReason: string;
  voidStatus: string;
  tipAmount?: number;
  referenceId: string;
  createdTime: string;
  operationId: string;
  processingFee: number;
  transactionNo: string;
  cardholderName: string;
  externalPaymentId: string;
}

export interface CloverVoid extends IdObject {
  amount: string;
  reason: string;
  orderId: string;
  deviceId: string;
  companyId: number;
  paymentId: string;
  checkoutId: number;
  employeeId: number;
  rawResponse: string;
  createdTime: string;
  cloverVoidId: string;
}

export interface CloverDevice extends IdObject {
  event: Event;
  name?: string;
  company: Company;
  deviceId: string;
  companyId: number;
}

export interface ShipStation extends IdObject {
  isActive: true;
  apiKey: string;
  secret: string;
  storeId: number;
  companyId: number;
}

export interface ShipStationOrder extends IdObject {
  gift: boolean;
  orderId: string;
  orderKey: string;
  orderDate: string;
  taxAmount: string;
  createdAt: string;
  updatedAt: string;
  billToName: string;
  billToCity: string;
  shipToName: string;
  shipToCity: string;
  createDate: string;
  modifyDate: string;
  orderTotal: string;
  amountPaid: string;
  billToState: string;
  shipToState: string;
  orderNumber: string;
  weightValue: string;
  weightUnits: string;
  orderStatus: string;
  confirmation: string;
  eventOrderId: number;
  shipmentCost: number;
  insuranceCost: number;
  billToStreet1: string;
  billToCountry: string;
  shipToStreet1: string;
  shipToStreet2: string;
  shipToCountry: string;
  customerEmail: string;
  shippingAmount: number;
  tagIds: number[] | null;
  dimensionsUnits: string;
  userId: Nullable<string>;
  billToPostalCode: string;
  shipToPostalCode: string;
  customerUsername: string;
  shipDate: Nullable<string>;
  shipToResidential: boolean;
  lastError: Nullable<string>;
  deletedAt: Nullable<string>;
  shipByDate: Nullable<string>;
  customerId: Nullable<string>;
  externallyFulfilled: boolean;
  giftMessage: Nullable<string>;
  billToPhone: Nullable<string>;
  shipToPhone: Nullable<string>;
  carrierCode: Nullable<string>;
  serviceCode: Nullable<string>;
  packageCode: Nullable<string>;
  paymentDate: Nullable<string>;
  shipToAddressVerified: string;
  items: ShipStationOrderItem[];
  dimensionsWidth: number | null;
  customerNotes: Nullable<string>;
  internalNotes: Nullable<string>;
  lastErrorDate: Nullable<string>;
  billToCompany: Nullable<string>;
  billToStreet2: Nullable<string>;
  billToStreet3: Nullable<string>;
  shipToCompany: Nullable<string>;
  shipToStreet3: Nullable<string>;
  paymentMethod: Nullable<string>;
  holdUntilDate: Nullable<string>;
  labelMessages: Nullable<string>;
  dimensionsLength: number | null;
  dimensionsHeight: number | null;
  trackingNumber: Nullable<string>;
  lastWebhookDate: Nullable<string>;
  lastWebhookData: Nullable<string>;
  lastWebhookType: Nullable<string>;
  billToResidential: boolean | null;
  externallyFulfilledBy: Nullable<string>;
  externallyFulfilledById: Nullable<string>;
  requestedShippingService: Nullable<string>;
  externallyFulfilledByName: Nullable<string>;
  advancedOptions: ShipStationAdvancedOptions;
  insuranceOptions: ShipStationInsuranceOptions;
  fullOrderResponse: ShipStationFullOrderResponse;
  internationalOptions: ShipStationInternationalOptions;
}

export interface ShipStationOrderItem {
  sku: string;
  name: string;
  options: any[];
  quantity: number;
  productId: number;
  unitPrice: number;
  createDate: string;
  modifyDate: string;
  adjustment: boolean;
  lineItemKey: string;
  orderItemId: number;
  upc: Nullable<string>;
  taxAmount: number | null;
  weight: ShipStationWeight;
  imageUrl: Nullable<string>;
  shippingAmount: number | null;
  fulfillmentSku: Nullable<string>;
  warehouseLocation: Nullable<string>;
}

export interface ShipStationWeight {
  units: string;
  value: number;
  WeightUnits: number;
}

export interface ShipStationAdvancedOptions {
  storeId: number;
  mergedIds: number[];
  warehouseId: number;
  mergedOrSplit: boolean;
  nonMachinable: boolean;
  parentId: number | null;
  source: Nullable<string>;
  containsAlcohol: boolean;
  saturdayDelivery: boolean;
  billToParty: Nullable<string>;
  customField1: Nullable<string>;
  customField2: Nullable<string>;
  customField3: Nullable<string>;
  billToAccount: Nullable<string>;
  billToPostalCode: Nullable<string>;
  billToCountryCode: Nullable<string>;
  billToMyOtherAccount: Nullable<string>;
}

export interface ShipStationInsuranceOptions {
  provider: string;
  insuredValue: number;
  insureShipment: boolean;
}

export interface ShipStationInternationalOptions {
  contents: Nullable<string>;
  customsItems: any[] | null;
  nonDelivery: Nullable<string>;
}

export interface ShipStationFullOrderResponse {
  gift: boolean;
  orderId: number;
  orderKey: string;
  orderDate: string;
  taxAmount: number;
  amountPaid: number;
  createDate: string;
  modifyDate: string;
  orderTotal: number;
  orderNumber: string;
  orderStatus: string;
  confirmation: string;
  customerEmail: string;
  dimensions: any | null;
  shippingAmount: number;
  tagIds: number[] | null;
  userId: Nullable<string>;
  customerUsername: string;
  weight: ShipStationWeight;
  shipDate: Nullable<string>;
  billTo: ShipStationAddress;
  shipTo: ShipStationAddress;
  customerId: Nullable<string>;
  shipByDate: Nullable<string>;
  externallyFulfilled: boolean;
  carrierCode: Nullable<string>;
  giftMessage: Nullable<string>;
  packageCode: Nullable<string>;
  paymentDate: Nullable<string>;
  serviceCode: Nullable<string>;
  items: ShipStationOrderItem[];
  customerNotes: Nullable<string>;
  holdUntilDate: Nullable<string>;
  internalNotes: Nullable<string>;
  labelMessages: Nullable<string>;
  paymentMethod: Nullable<string>;
  externallyFulfilledBy: Nullable<string>;
  externallyFulfilledById: Nullable<string>;
  requestedShippingService: Nullable<string>;
  externallyFulfilledByName: Nullable<string>;
  advancedOptions: ShipStationAdvancedOptions;
  insuranceOptions: ShipStationInsuranceOptions;
  internationalOptions: ShipStationInternationalOptions;
}

export interface ShipStationAddress {
  city: string;
  name: string;
  state: string;
  country: string;
  street1: string;
  postalCode: string;
  phone: Nullable<string>;
  company: Nullable<string>;
  street2: Nullable<string>;
  street3: Nullable<string>;
  residential: boolean | null;
  addressVerified: Nullable<string>;
}

export interface PaymentIntent extends IdObject {
  recoveryAction?: 'OPEN_CLOVER_APP' | 'OWNER_REVIEW';
  // Server parked this sale after the terminal connection was lost (Owner 13 Oct).
  parked?: boolean;
  // Clover could not reach the terminal on the last check (Owner 13 Oct).
  terminalUnreachable?: boolean;
  signaturePending?: boolean;
  isProcessingInClover?: boolean;
  approvalConfirmed?: boolean;
  canCancel?: boolean;
  recoveryStage?:
    | 'SIGNATURE_REQUIRED'
    | 'SIGNATURE_CAPTURING'
    | 'SIGNATURE_DELIVERY_UNKNOWN'
    | 'SIGNATURE_SAVE_PENDING'
    | 'CONFIRMATION_REQUIRED';
  amount: number;
  userId: number;
  eventId: number;
  companyId: string;
  canRetry: boolean;
  completedAt: string;
  processingFee: number;
  cloverDeviceId: number;
  paymentIntentId: number;
  cloverPaymentId?: string;
  externalPaymentId: string;
  failedAt: Nullable<string>;
  status: PaymentIntentStatus;
  tipAmount: Nullable<number>;
  errorAction: Nullable<string>;
  cloverPayment?: CloverPayment;
  errorMessage: Nullable<string>;
  receiptEmail: Nullable<string>;
  shouldResetExternalId: boolean;
}

export interface ShippingTier extends IdObject {
  price: string;
  minItems: number;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  maxItems: number | null;
}

export interface FormatCaseInfo<T> {
  key: keyof T;
  title: string;
  fallback?: string;
  getValue?: (data: T) => void;
  format?: (value: string | number) => string;
}

export interface FiservChargeback extends IdObject {
  cbType: string;
  status: string;
  amount: string;
  dueDate: string;
  linkedAt: string;
  syncedAt: string;
  cardType: string;
  currency: string;
  cardLast4: string;
  createdAt: string;
  updatedAt: string;
  companyId: number;
  checkoutId: number;
  chargebackId: number;
  caseNumbers: string[];
  externalIds: string[];
  chargebackDate: string;
  transactionDate: string;
  referenceNumber: string;
  cbTypeDescription: string;
  deletedAt: Nullable<string>;
  reasonText: Nullable<string>;
  reasonCode: Nullable<string>;
  merchantId: Nullable<number>;
  preDispute: Nullable<string>;
  responseDate: Nullable<string>;
  rawDataHistory: FiservCaseDetail[];
  reasonDescription: Nullable<string>;
  authorizationCode: Nullable<string>;
}
